"use client";

import { useState } from "react";
import { Button } from "@/components/ui/button";

export interface SearchCriteriaEditorProps {
  initialLabel?: string;
  initialDescription?: string;
  onCancel: () => void;
  onSaved: (item: { label: string; description: string }) => void;
}

const LABEL_MAX = 120;
const DESCRIPTION_MAX = 2000;

export function SearchCriteriaEditor({
  initialLabel = "",
  initialDescription = "",
  onCancel,
  onSaved,
}: SearchCriteriaEditorProps) {
  const [label, setLabel] = useState(initialLabel);
  const [description, setDescription] = useState(initialDescription);

  const labelTrim = label.trim();
  const descriptionTrim = description.trim();
  const labelInvalid = labelTrim.length === 0 || labelTrim.length > LABEL_MAX;
  const descInvalid =
    descriptionTrim.length === 0 || descriptionTrim.length > DESCRIPTION_MAX;
  const disabled = labelInvalid || descInvalid;

  const handleSave = () => {
    if (disabled) return;
    onSaved({ label: labelTrim, description: descriptionTrim });
  };

  return (
    <div className="flex w-full flex-col gap-3 p-4">
      <div className="flex flex-col gap-1">
        <label htmlFor="search-criteria-label" className="text-xs font-medium text-heading">
          Criteria Label
        </label>
        <input
          id="search-criteria-label"
          type="text"
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          maxLength={LABEL_MAX + 1}
          placeholder="ML/DL Work Experience"
          autoFocus
          className="w-full rounded-md border border-contrast/15 bg-contrast/4 dark:bg-[#18181a] px-3 py-2 text-sm text-heading placeholder:text-contrast/40 outline-none transition-colors focus:border-primary"
        />
        <span className="self-end text-[11px] text-contrast/40 tabular-nums">
          {labelTrim.length}/{LABEL_MAX}
        </span>
      </div>
      <div className="flex flex-col gap-1">
        <label
          htmlFor="search-criteria-description"
          className="text-xs font-medium text-heading"
        >
          Criteria Requirement
        </label>
        <textarea
          id="search-criteria-description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          maxLength={DESCRIPTION_MAX + 1}
          rows={3}
          placeholder="The profile must have 8+ years of experience in Machine Learning & Deep Learning."
          className="w-full max-h-32 resize-y rounded-md border border-contrast/15 bg-contrast/4 dark:bg-[#18181a] px-3 py-2 text-sm text-heading placeholder:text-contrast/40 outline-none transition-colors focus:border-primary"
        />
        <span className="self-end text-[11px] text-contrast/40 tabular-nums">
          {descriptionTrim.length}/{DESCRIPTION_MAX}
        </span>
      </div>
      <div className="flex items-center justify-end gap-2">
        <Button type="button" variant="secondary" size="sm" onClick={onCancel}>
          Cancel
        </Button>
        <Button
          type="button"
          variant="primary"
          size="sm"
          onClick={handleSave}
          disabled={disabled}
        >
          Save
        </Button>
      </div>
    </div>
  );
}
